import '@milkdown/crepe/theme/common/style.css'
import '@milkdown/crepe/theme/nord.css'
import '@/styles/MarkdownEditor.css'

import { Alert, Textarea } from '@mantine/core'
import { Crepe } from '@milkdown/crepe'
import { replaceAll } from '@milkdown/utils'
import { IconAlertTriangle } from '@tabler/icons-react'
import React, { useEffect, useRef, useState } from 'react'

export interface MarkdownEditorProps {
  /** Initial markdown content */
  defaultValue?: string
  /** Current markdown content for controlled usage */
  value?: string
  /** Called when content changes */
  onChange?: (markdown: string) => void
  /** Whether the editor is read-only */
  readOnly?: boolean
  /** CSS class name */
  className?: string
  /** Inline styles */
  style?: React.CSSProperties
}

/**
 * MarkdownEditor component with fallback support
 *
 * This component uses Milkdown/Crepe for rich markdown editing, but gracefully
 * falls back to a textarea if the editor fails to initialize.
 *
 * States:
 * - 'loading': Initial state while editor initializes
 * - 'ready': Editor loaded successfully
 * - 'error': Editor failed to load, shows fallback textarea with alert
 */

export function MarkdownEditor(props: MarkdownEditorProps) {
  const { defaultValue = '', value, onChange, readOnly = false, className, style } = props

  const containerRef = useRef<HTMLDivElement | null>(null)
  const crepeRef = useRef<Crepe | null>(null)
  const currentValueRef = useRef<string>(value ?? defaultValue)
  const onChangeRef = useRef(onChange)

  // Track editor state for fallback
  const [editorState, setEditorState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [fallbackValue, setFallbackValue] = useState<string>(value ?? defaultValue)

  // Keep onChange callback current
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // Handle fallback textarea changes
  const handleFallbackChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value
    setFallbackValue(newValue)
    currentValueRef.current = newValue
    onChangeRef.current?.(newValue)
  }

  // Initialize Crepe editor
  useEffect(() => {
    if (!containerRef.current) return

    const initialContent = value ?? defaultValue
    const crepe = new Crepe({
      root: containerRef.current,
      defaultValue: initialContent,
    })

    crepeRef.current = crepe
    currentValueRef.current = initialContent

    crepe
      .on(listener => {
        listener.markdownUpdated((_, markdown) => {
          currentValueRef.current = markdown
          onChangeRef.current?.(markdown)
        })
      })
      .create()
      .then(() => {
        crepe.setReadonly(readOnly)
        setEditorState('ready')
      })
      .catch(error => {
        console.error('Failed to initialize markdown editor:', error)
        setEditorState('error')
        // Set fallback value to current content
        setFallbackValue(currentValueRef.current)
      })

    return () => {
      // Only destroy if crepe was successfully created
      try {
        crepe.destroy()
      } catch (error) {
        console.warn('Error destroying markdown editor:', error)
      }
      crepeRef.current = null
    }
  }, []) // Only run once on mount

  // Handle controlled value changes
  useEffect(() => {
    if (editorState === 'error') {
      // Update fallback value when props change
      if (value !== undefined && value !== fallbackValue) {
        setFallbackValue(value)
      }
      return
    }

    const crepe = crepeRef.current
    if (!crepe || value === undefined) return

    if (value !== currentValueRef.current) {
      crepe.editor.action(replaceAll(value))
      currentValueRef.current = value
    }
  }, [value, editorState, fallbackValue])

  // Handle readOnly changes
  useEffect(() => {
    if (editorState !== 'ready') return

    const crepe = crepeRef.current
    if (!crepe) return
    crepe.setReadonly(readOnly)
  }, [readOnly, editorState])

  // Show fallback textarea if editor failed to load
  if (editorState === 'error') {
    return (
      <div className={className} style={style}>
        <Alert
          icon={<IconAlertTriangle size="1rem" />}
          title="Markdown Editor Unavailable"
          color="yellow"
          variant="light"
          mb="md">
          The rich text editor failed to load. You can still edit your content using the text area below.
        </Alert>
        <Textarea
          value={fallbackValue}
          onChange={handleFallbackChange}
          disabled={readOnly}
          placeholder="Enter your markdown content here..."
          autosize
          minRows={10}
          resize="vertical"
          style={{ minHeight: '300px' }}
        />
      </div>
    )
  }

  return (
    <div className={className} style={style}>
      {editorState === 'loading' && (
        <div style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Loading editor...
        </div>
      )}
      <div
        ref={containerRef}
        className="markdown-content"
        style={{ display: editorState === 'ready' ? 'block' : 'none' }}
      />
    </div>
  )
}
